import type { CollectionSummary, DeclaredMemberType, MeResponse } from "@ondo/shared";
import { toggleDeclaredType } from "@ondo/shared";
import { useEffect, useState } from "react";

import { declareTypes, fetchCollections } from "../lib/api.ts";
import { describeApiError } from "../lib/apiError.ts";
import { DECLARE_COPY } from "./workspace.constants.ts";
import {
  declarationOptions,
  defaultDeclaration,
  describeEmptyChooser,
  newDeclarations,
} from "./workspace.model.ts";
import type { DeclarationOption, EmptyChooserNote } from "./workspace.model.ts";

/**
 * The empty chooser's door: say what you do, and a workspace opens — OND-12.
 *
 * The write is the existing `PUT /v1/me/declared-types`, and the whole of the
 * recovery is that it answers with a fresh `MeResponse`. Handing that to
 * `replaceMe` re-derives `workspaces` from what the SERVER says the account now
 * is, so the chooser that was empty fills itself on the same render — this
 * controller never decides that a workspace exists, it only asks.
 *
 * `collections` is fetched here, and only here, because the two sentences the
 * ticket insists on telling apart — holding nothing, and holding a collection
 * somebody shared — are not on `MeResponse`. A failed read is kept as `null`
 * rather than treated as empty, for the model's reason: the stronger claim
 * waits for the fact.
 */
export interface DeclareCapacityView {
  note: EmptyChooserNote;
  options: DeclarationOption[];
  /** Something new is ticked. The button is otherwise disabled, with the reason beside it. */
  canSubmit: boolean;
  busy: boolean;
  error: string | null;
}

export function useDeclareCapacityController(input: {
  me: MeResponse;
  replaceMe: (me: MeResponse) => void;
}): {
  view: DeclareCapacityView;
  toggle: (type: DeclaredMemberType) => void;
  submit: () => void;
} {
  const held = input.me.person.declaredTypes;
  const [selected, setSelected] = useState<DeclaredMemberType[]>(() => defaultDeclaration(held));
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    fetchCollections()
      .then((collections) => {
        if (active) setCollections(collections);
      })
      .catch(() => {
        // Left null on purpose: the sentence for "could not read" exists so
        // this never has to guess between the other two.
        if (active) setCollections(null);
      });

    return () => {
      active = false;
    };
  }, []);

  const additions = newDeclarations(held, selected);

  return {
    view: {
      note: describeEmptyChooser(input.me.principal.displayName, collections),
      options: declarationOptions(held, selected),
      canSubmit: additions.length > 0 && !busy,
      busy,
      error,
    },
    toggle: (type) => {
      // A held type is not toggleable: the route cannot remove it, and the
      // option is drawn disabled — this is the model's rule restated for a
      // handler that a test or a stale DOM could still reach.
      if (held.includes(type)) return;
      setSelected((current) => toggleDeclaredType(current, type));
      setError(null);
    },
    submit: () => {
      if (additions.length === 0 || busy) return;
      setBusy(true);
      setError(null);

      declareTypes({ declaredTypes: additions })
        .then((me) => {
          // The response IS the read. `replaceMe` puts it where every screen
          // looks, and the page re-resolves its view from the new workspaces.
          input.replaceMe(me);
        })
        .catch((cause: unknown) => {
          setError(describeApiError(cause, DECLARE_COPY.failed));
        })
        .finally(() => setBusy(false));
    },
  };
}
